import { useEffect, useState } from 'react';
import { api, type DataSource } from '../lib/api';
import { AppNav } from '../components/AppNav';

type IssuedCredentials = {
  id: string;
  name: string;
  host: string; port: number; database: string; schema: string;
  username: string; password: string;
  sshTunnel: string;
};

export function DataSources() {
  const [items, setItems]       = useState<DataSource[]>([]);
  const [creating, setCreating] = useState<'internal' | 'external' | null>(null);
  const [internalForm, setInternalForm] = useState({ name: '', slug: '' });
  const [externalForm, setExternalForm] = useState({
    name: '', host: '', port: 5432, databaseName: '', userName: '', password: '',
    defaultSchema: 'public', sslMode: 'require' as const,
  });
  const [err, setErr]       = useState<string | null>(null);
  const [probe, setProbe]   = useState<{ id: string; ok: boolean; msg: string } | null>(null);
  const [issued, setIssued] = useState<IssuedCredentials | null>(null);

  useEffect(() => { reload(); }, []);
  async function reload() {
    const r = await api.listDataSources();
    setItems(r.items);
  }

  async function createExternal() {
    setErr(null);
    try {
      await api.createDataSource(externalForm);
      setCreating(null);
      setExternalForm({ name: '', host: '', port: 5432, databaseName: '', userName: '', password: '', defaultSchema: 'public', sslMode: 'require' });
      reload();
    } catch (e: any) { setErr(String(e.message)); }
  }

  async function createInternal() {
    setErr(null);
    try {
      const r = await api.provisionInternalSchema({
        name: internalForm.name,
        slug: internalForm.slug || undefined,
      });
      setCreating(null);
      setInternalForm({ name: '', slug: '' });
      setIssued({
        id: r.id, name: r.name,
        host: r.credentials.host, port: r.credentials.port, database: r.credentials.database,
        schema: r.credentials.schema, username: r.credentials.username, password: r.credentials.password,
        sshTunnel: r.instructions.ssh_tunnel,
      });
      reload();
    } catch (e: any) { setErr(String(e.message)); }
  }

  async function testConn(id: string) {
    setProbe({ id, ok: false, msg: 'Probando…' });
    try {
      const r = await api.probeDataSource(id);
      setProbe({ id, ok: !!r.ok, msg: r.ok ? `OK · ${r.tables?.length ?? 0} tablas con geometría visibles` : `FALLO: ${r.error}` });
    } catch (e: any) { setProbe({ id, ok: false, msg: String(e.message) }); }
  }

  async function del(id: string) {
    if (!confirm('Eliminar fuente? Si es un schema interno auto-aprovisionado, también se borrará el schema y el rol PG (con todas sus tablas).')) return;
    await api.deleteDataSource(id);
    reload();
  }

  return (
    <div>
      <AppNav />
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>Fuentes de datos</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setCreating('internal'); setErr(null); }} style={btn('primary')}>+ Schema interno</button>
            <button onClick={() => { setCreating('external'); setErr(null); }} style={btn('ghost')}>+ DB externa</button>
          </div>
        </header>
        <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
          <strong>Schema interno</strong>: el sistema crea un schema + rol PostgreSQL en <code>atlas_goberna_db</code> con password generado automático.
          El cartógrafo lo usa desde su pgAdmin para subir tablas/views, y la plataforma las muestra como capas. <br/>
          <strong>DB externa</strong>: conectar a una base PostgreSQL ya existente (RDS, otro VPS, etc).
        </p>

        {/* ───────── Modal credenciales emitidas (one-time) ───────── */}
        {issued && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'white', maxWidth: 600, width: '90%', borderRadius: 8, padding: 24, boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
              <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Credenciales generadas — copialas ahora</h2>
              <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 16px' }}>
                ⚠ El password se muestra UNA SOLA VEZ. Guardalo en un gestor de contraseñas. Si lo perdés, hay que rotar.
              </p>
              <CredField label="Schema"   value={issued.schema} />
              <CredField label="Host"     value={issued.host} />
              <CredField label="Puerto"   value={String(issued.port)} />
              <CredField label="Database" value={issued.database} />
              <CredField label="Usuario"  value={issued.username} />
              <CredField label="Password" value={issued.password} secret />
              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />
              <h3 style={{ fontSize: 13, margin: '0 0 6px', textTransform: 'uppercase', color: '#475569' }}>Conexión segura por SSH tunnel (recomendado)</h3>
              <p style={{ fontSize: 12, color: '#475569', margin: '0 0 6px' }}>En tu terminal local, abrí el túnel:</p>
              <CredField label="Comando" value={issued.sshTunnel} mono />
              <p style={{ fontSize: 12, color: '#475569', margin: '8px 0 4px' }}>Después en pgAdmin: host=<code>localhost</code>, port=<code>15432</code>, db=<code>{issued.database}</code>, user=<code>{issued.username}</code>, password=el de arriba.</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button onClick={() => setIssued(null)} style={btn('primary')}>Listo, lo guardé</button>
              </div>
            </div>
          </div>
        )}

        {/* ───────── Form schema interno ───────── */}
        {creating === 'internal' && (
          <div style={{ border: '1px solid #cbd5e1', padding: 16, borderRadius: 8, marginBottom: 16, display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
            <Input label="Nombre (display)"  v={internalForm.name} on={v => setInternalForm({ ...internalForm, name: v })} placeholder="Carto del cliente X" full />
            <Input label="Slug (opcional)"   v={internalForm.slug} on={v => setInternalForm({ ...internalForm, slug: v })} placeholder="(auto: gp_xxxxxx)" full />
            <p style={{ gridColumn: '1 / -1', fontSize: 12, color: '#475569', margin: 0 }}>
              Se creará un schema <code>{internalForm.slug || 'gp_xxxxxx'}</code> y un rol <code>gp_user_{internalForm.slug || 'xxxxxx'}</code> con password fuerte. Slug: lowercase, números, guión bajo.
            </p>
            {err && <p style={{ gridColumn: '1 / -1', color: '#dc2626', fontSize: 13, margin: 0 }}>{err}</p>}
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
              <button onClick={createInternal} disabled={!internalForm.name} style={btn('primary')}>Aprovisionar</button>
              <button onClick={() => setCreating(null)} style={btn('ghost')}>Cancelar</button>
            </div>
          </div>
        )}

        {/* ───────── Form DB externa ───────── */}
        {creating === 'external' && (
          <div style={{ border: '1px solid #cbd5e1', padding: 16, borderRadius: 8, marginBottom: 16, display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
            <Input label="Nombre"     v={externalForm.name}         on={v => setExternalForm({...externalForm, name: v})} full />
            <Input label="Host"       v={externalForm.host}         on={v => setExternalForm({...externalForm, host: v})} placeholder="db.cliente.com" />
            <Input label="Puerto"     v={String(externalForm.port)} on={v => setExternalForm({...externalForm, port: +v || 5432})} type="number" />
            <Input label="Database"   v={externalForm.databaseName} on={v => setExternalForm({...externalForm, databaseName: v})} />
            <Input label="Usuario"    v={externalForm.userName}     on={v => setExternalForm({...externalForm, userName: v})} />
            <Input label="Password"   v={externalForm.password}     on={v => setExternalForm({...externalForm, password: v})} type="password" />
            <Input label="Schema"     v={externalForm.defaultSchema} on={v => setExternalForm({...externalForm, defaultSchema: v})} placeholder="public" />
            <label>
              <span style={{ display: 'block', fontSize: 11, color: '#475569', marginBottom: 4 }}>SSL Mode</span>
              <select value={externalForm.sslMode} onChange={e => setExternalForm({ ...externalForm, sslMode: e.target.value as any })} style={inp}>
                <option value="disable">disable</option>
                <option value="require">require</option>
                <option value="verify-full">verify-full</option>
              </select>
            </label>
            {err && <p style={{ gridColumn: '1 / -1', color: '#dc2626', fontSize: 13, margin: 0 }}>{err}</p>}
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
              <button onClick={createExternal} style={btn('primary')}>Crear</button>
              <button onClick={() => setCreating(null)} style={btn('ghost')}>Cancelar</button>
            </div>
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>
            <th style={th}>Nombre</th><th style={th}>Tipo</th><th style={th}>Host</th><th style={th}>Schema</th><th style={th}>Usuario</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {items.map(ds => (
              <tr key={ds.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={td}>{ds.name}</td>
                <td style={td}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: ds.kind === 'internal' ? '#0ea5e91a' : '#f1f5f9', color: ds.kind === 'internal' ? '#0369a1' : '#475569' }}>{ds.kind}</span></td>
                <td style={td}>{ds.host ?? '—'}{ds.port ? `:${ds.port}` : ''}</td>
                <td style={td}><code>{ds.defaultSchema}</code></td>
                <td style={td}><code style={{ fontSize: 11 }}>{ds.userName ?? '—'}</code></td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <button onClick={() => testConn(ds.id)} style={btn('ghost')}>Probar</button>
                  <button onClick={() => del(ds.id)} style={{ ...btn('ghost'), color: '#dc2626', borderColor: '#fecaca', marginLeft: 4 }}>Eliminar</button>
                  {probe?.id === ds.id && (
                    <div style={{ marginTop: 4, fontSize: 11, color: probe.ok ? '#15803d' : '#dc2626' }}>{probe.msg}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CredField({ label, value, mono, secret }: { label: string; value: string; mono?: boolean; secret?: boolean }) {
  const [revealed, setRevealed] = useState(!secret);
  const [copied, setCopied]     = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 11, color: '#64748b', minWidth: 80 }}>{label}</span>
      <code style={{
        flex: 1, padding: '4px 8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4,
        fontFamily: mono ? 'ui-monospace, SF Mono, monospace' : 'inherit',
        fontSize: mono ? 11 : 12, overflowX: 'auto', whiteSpace: 'nowrap',
      }}>
        {revealed ? value : '••••••••••••••••'}
      </code>
      {secret && (
        <button onClick={() => setRevealed(r => !r)} style={btn('ghost')}>{revealed ? 'Ocultar' : 'Ver'}</button>
      )}
      <button onClick={copy} style={btn('ghost')}>{copied ? '✓' : 'Copy'}</button>
    </div>
  );
}

function Input(p: { label: string; v: string; on: (s: string) => void; type?: string; placeholder?: string; full?: boolean }) {
  return (
    <label style={{ gridColumn: p.full ? '1 / -1' : 'auto' }}>
      <span style={{ display: 'block', fontSize: 11, color: '#475569', marginBottom: 4 }}>{p.label}</span>
      <input value={p.v} onChange={e => p.on(e.target.value)} type={p.type ?? 'text'} placeholder={p.placeholder} style={inp} />
    </label>
  );
}

const inp: React.CSSProperties = { width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 4, boxSizing: 'border-box', fontSize: 13 };
const th: React.CSSProperties = { padding: '8px 10px', fontWeight: 500 };
const td: React.CSSProperties = { padding: '8px 10px' };
const btn = (k: 'primary' | 'ghost'): React.CSSProperties => k === 'primary'
  ? { padding: '6px 12px', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
  : { padding: '4px 10px', background: 'white', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, cursor: 'pointer' };
